import React, { useState } from "react";
import { ArrowLeft, Home, Loader2, Wallet } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { fetchCustomerData } from "@/redux/slices/customerSlice";
import { dynamicAPI, RedeemVoucher, transactionredeem, transferBalance } from "@/services/customer";
import { getT } from "@/i18n/translations";


const UpiConfirmPage = () => {
  const t = getT();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { upiId, upiName, defaultBank } = location.state || {};
  const { data: customer } = useSelector((state) => state.customer);

  const vouchers = customer?.WalletSummary?.filter((item) => item.type === "voucher") || [];
  const availableVouchers = vouchers.filter((item) => item.VoucherUsed === false);
  const available = customer?.walletsDetail?.availableValue || 0


  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [transferredAmount, setTransferredAmount] = useState(0);
  const [transferWarning, setTransferWarning] = useState(false);

  const handleFinalSubmit = async () => {
    if (available === 0) return;
    setLoading(true);

    try {
      const token_gen = JSON.parse(localStorage.getItem("token_gen"));
      const customerCode = localStorage.getItem("customercode");
      const now = new Date().toISOString();

      const body = {
        customer: {
          customercode: customerCode
        },
        redeemDetail: {
          walletType: "point",
          values: available,
          transactionType: "redeem",
          transactionDate: now
        }
      }
      let response = await transactionredeem(token_gen?.access_token, body);
      if (response?.message !== 'Request processed successfully.') {
        toast.error(response?.message);
        setLoading(false);
        return;
      }

      try {
        const metaPayload = {
          customer: {
            customerCode,
            name: customer.customer?.name,
            mobile: customer.customer?.mobile
          },
          vouchers: availableVouchers.map(v => ({
            voucherCode: v.voucherCode,
            voucherTemplateCode: v.voucherTemplateCode,
            earnedDate: v.earnedDate
          })),
          payment: upiId
            ? {
              type: "upi",
              upiId,
              upiName: upiName || "",
              totalAmount: available,
            }
            : defaultBank
              ? {
                type: "bank",
                bankName: defaultBank.bankName,
                accountNumber: defaultBank.accountNumber,
                ifscCode: defaultBank.ifscCode,
                beneficiaryName:
                  defaultBank.nickname || defaultBank.bankName,
                totalAmount: available,
              }
              : {},
          transactionDate: now,
          merchantId: customer.customer.merchantId[0]
        };

        await dynamicAPI(token_gen?.access_token, metaPayload);
      } catch (err) {
        console.error("Meta API failed", err);
      }

      await dispatch(fetchCustomerData(customerCode));

      setTransferredAmount(available);
      setShowPopup(true);

    } catch (err) {
      console.log(err)
      toast.error(t.confirm_errGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center"
      style={{
        fontFamily: "'Poppins', sans-serif",
        backgroundColor: "#fff5f5",
        backgroundImage:
          "radial-gradient(circle, #ffd6d6 1px, transparent 1px)",
        backgroundSize: "22px 22px",
      }}
    >
      {loading && (
        <div className="fixed inset-0 bg-black/30 flex justify-center items-center z-50 backdrop-blur-sm">
          <Loader2 className="animate-spin h-10 w-10 text-[#ED1C16]" />
        </div>
      )}

      <div className="w-full max-w-md min-h-screen flex flex-col bg-[#ffe5e5]">

        {/* HEADER */}
        <div className="relative flex items-center justify-center px-4 py-4 mt-2">
          <button
            onClick={() => navigate(-1)}
            className="absolute left-4 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-sm border border-gray-100"
          >
            <ArrowLeft className="w-5 h-5 text-[#ED1C16]" />
          </button>

          <p className="text-[15px] font-semibold text-black">
            {t.confirm_pageTitle}
          </p>

          <button
            onClick={() => navigate("/home")}
            className="absolute right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-sm border border-gray-100"
          >
            <Home className="w-5 h-5 text-[#ED1C16]" />
          </button>
        </div>

        <div className="px-4 pb-8 space-y-3 flex-1">

          {/* AMOUNT CARD */}
          <div className="bg-white rounded-2xl px-5 py-5 shadow-sm border border-gray-100 mx-1 text-center">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1">
              {t.confirm_transferAmount}
            </p>

            <p className="text-[48px] font-bold text-[#ED1C16] leading-tight">
              ₹{available}
            </p>

            {available === 0 && (
              <p className="text-[12px] text-red-500 mt-2">
                {t.confirm_noBalance}
              </p>
            )}
          </div>

          {/* UPI CARD */}
          {upiId && (
            <div className="bg-white rounded-2xl px-5 py-4 shadow-sm border border-gray-100 mx-1">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-3">
                {t.confirm_paymentMethod}
              </p>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#ED1C16"
                    strokeWidth="2"
                  >
                    <rect x="5" y="2" width="14" height="20" rx="2" />
                    <line x1="12" y1="18" x2="12.01" y2="18" />
                  </svg>
                </div>

                <div>
                  <p className="text-[13px] font-bold text-black">
                    {t.confirm_upi}
                  </p>
                  {upiName && (
                    <p className="text-[12px] text-gray-500">
                      {upiName}
                    </p>
                  )}
                  <p className="text-[12px] text-gray-400">{upiId}</p>
                </div>
              </div>
            </div>
          )}

          {/* BANK CARD */}
          {defaultBank && (
            <div className="bg-white rounded-2xl px-5 py-4 shadow-sm border border-gray-100 mx-1">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-3">
                {t.confirm_paymentMethod}
              </p>

              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#ED1C16"
                    strokeWidth="2"
                  >
                    <rect x="2" y="5" width="20" height="14" rx="2" />
                    <path d="M16 12h.01" />
                  </svg>
                </div>

                <p className="text-[13px] font-bold text-black">
                  {t.confirm_bankTransfer}
                </p>
              </div>

              <div className="space-y-1.5 border-t border-gray-100 pt-3">
                {[
                  { label: t.confirm_bank, value: defaultBank.bankName },
                  { label: t.confirm_account, value: `****${defaultBank.accountNumber?.slice(-4)}` },
                  { label: t.confirm_ifsc, value: defaultBank.ifscCode },
                  { label: t.confirm_name, value: defaultBank.nickname },
                ].map(({ label, value }) => (
                  <div key={label} className="flex justify-between">
                    <p className="text-[12px] text-gray-400">{label}</p>
                    <p className="text-[12px] font-semibold text-black">
                      {value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CONFIRM BUTTON */}
          <button
            onClick={handleFinalSubmit}
            disabled={loading || available === 0}
            className="w-full py-4 rounded-xl text-[15px] font-bold text-white active:scale-95 transition disabled:opacity-50 mx-1"
            style={{ backgroundColor: "#ED1C16" }}
          >
            {available === 0
              ? t.confirm_btnNoBalance
              : t.confirm_btnConfirm}
          </button>
        </div>

        {/* FOOTER */}
        {/* <div className="w-full py-3 border-t border-gray-200 mt-auto">
          <p className="text-[13px] text-black uppercase text-center tracking-wide">
            © {new Date().getFullYear()} Consumer Journeys. All Rights Reserved.
          </p>
        </div> */}
      </div>

      {/* SUCCESS POPUP */}
      {showPopup && (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-center items-center px-4">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-xl p-7 text-center">

            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-red-50 border border-red-200">
              <Wallet className="w-7 h-7 text-[#ED1C16]" />
            </div>

            <p className="text-[11px] font-semibold text-gray-500 uppercase mb-1">
              {t.confirm_popupLabel}
            </p>

            <h2 className="text-[14px] font-bold text-black mb-1">
              {t.confirm_popupTitle}
            </h2>

            <p className="text-[10px] text-gray-400 mb-4">
              {t.confirm_popupMsg}
            </p>

            <div className="rounded-2xl py-4 px-5 mb-4 bg-gray-50 border border-gray-100">
              <p className="text-[36px] font-bold text-[#ED1C16]">
                ₹{transferredAmount}
              </p>
              <p className="text-[12px] text-gray-500 mt-1">
                {t.confirm_popupTotal}
              </p>
            </div>

            {transferWarning && (
              <div className="rounded-xl px-4 py-3 mb-4 text-left bg-yellow-50 border border-yellow-200">
                <p className="text-[12px] font-semibold text-yellow-700 mb-0.5">
                  {t.confirm_warningTitle}
                </p>
                <p className="text-[11px] text-yellow-600">
                  {t.confirm_warningMsg}
                </p>
              </div>
            )}

            <button
              onClick={() => navigate("/home", { replace: true })}
              className="w-full py-3.5 rounded-xl text-white font-bold"
              style={{ backgroundColor: "#ED1C16" }}
            >
              {t.confirm_done}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpiConfirmPage;